import { useState } from "react";
import { useNavigate } from "react-router";

import AddIcon from "@mui/icons-material/Add";
import { Box, Button, Checkbox, FormControlLabel, Stack, Typography } from "@mui/material";

import { DataTable } from "@/common/components/table/DataTable";
import { UserPermission } from "@/common/contexts/current-user-context/CurrentUserContextProvider.types";
import withAuthorization from "@/common/hocs/with-authorization/withAuthorization";
import { dutySchedulePath } from "@/router/DutyScheduleRoutes";
import { useTranslation } from "@/translations";

import { DutyScheduleCreationDialog } from "./components/create-dialog/DutyScheduleCreationDialog";
import { useDutyScheduleDelete } from "./hooks/useDutyScheduleDelete";
import { useDutyScheduleRelease } from "./hooks/useDutyScheduleRelease";
import { useDutySchedules } from "./hooks/useDutySchedules";

function DutyScheduleInner() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  const { confirmAndRelease, isReleasing } = useDutyScheduleRelease();
  const { confirmAndDelete, isDeleting } = useDutyScheduleDelete();

  const { dutySchedules, isLoading, columns, showPast, setShowPast } = useDutySchedules({
    onRelease: confirmAndRelease,
    onDelete: confirmAndDelete,
    isBusy: isReleasing || isDeleting,
  });

  return (
    <Stack spacing={1} sx={{ height: "100%" }} pb={3} overflow={"hidden"}>
      <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
        <Typography variant="h1">{t("dutySchedule.title")}</Typography>
        <Box display="flex" alignItems="center" gap={2}>
          <FormControlLabel
            control={
              <Checkbox
                checked={showPast}
                onChange={(_, checked) => setShowPast(checked)}
                aria-label={t("dutySchedule.showPast")}
              />
            }
            label={t("dutySchedule.showPast")}
          />
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setIsCreateDialogOpen(true)}>
            {t("dutySchedule.createButton")}
          </Button>
        </Box>
      </Box>
      <Stack sx={{ overflow: "auto", flex: 1, minHeight: 0 }}>
        <DataTable
          data={dutySchedules}
          columns={columns}
          isLoading={isLoading}
          getRowId={(row) => row.id}
          onRowClick={(id) => void navigate(dutySchedulePath.byId({ id }))}
          hideNoRowsOverlay
          initialSortModel={[{ field: "periodStart", sort: "desc" }]}
        />
      </Stack>
      {isCreateDialogOpen && <DutyScheduleCreationDialog onClose={() => setIsCreateDialogOpen(false)} />}
    </Stack>
  );
}

export const DutySchedule = withAuthorization(DutyScheduleInner, UserPermission.Supervisor);
